/**
 * Derive a create-form config (the `createProperties` / `createAssociations`
 * name lists that `buildGenericCreateConfig` consumes) from the live CRM schema, for any
 * entity type — including custom `org_*` types that have no static registry
 * `createConfig`.
 *
 * This is the schema-driven fallback that lets the association picker's inline
 * "Create new …" affordance work for every type the user can create, rather
 * than only the three types (contact/company/deal) hardcoded in the registry.
 * Mirrors the dynamic derivation already done in `GenericCrmList`.
 */

import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import {
  associationMatchesSource,
  counterpartEntityKinds,
  getEntityKinds,
} from '@/lib/crm/utils/associationMatching';
import { hasPropertyEditor } from '@/lib/crm/utils/propertyEditability';

/**
 * Entity kinds a custom (`org_*`) entity type's create form offers as
 * associations. The create form is scoped to domain relationships; a
 * counterpart whose kinds don't intersect this set is filtered out — most
 * notably the kind-based "Reminders" association (its counterpart,
 * ahoy_reminder, is event-kind, not in this set). Notes/tasks survive because
 * ahoy_note is activity-kind. System (ahoy_*) entity types are left untouched,
 * so their create forms still show every association (e.g. a contact's deal
 * associations). Shared by `CrmCreateForm` (which applies it) and the record
 * overview (which mirrors the create form's field set for its placeholders).
 */
export const CREATE_FORM_DOMAIN_KINDS: ReadonlySet<string> = new Set([
  'asset',
  'party',
  'principal',
  'case',
  'activity',
]);

/** The minimal property-schema shape these helpers read. */
export interface CreateConfigPropertySchema {
  property_type_name: string;
  field_type_name: string;
  value_write_access: 'all' | 'internal' | 'none';
}

/**
 * Names of the properties to show in a create form: every user-writable
 * property, with `ahoy_display_name` (the record "Name") pulled to the front.
 * System-managed properties (`value_write_access` of `internal`/`none`, e.g.
 * `ahoy_created_at`, `ahoy_hidden`) are excluded, as are properties whose
 * field type has no PropertyInput editor (e.g. json) — offering them renders
 * a guaranteed-broken "Unknown field type" input.
 */
export function deriveCreatePropertyNames(
  schema: Record<string, CreateConfigPropertySchema>
): string[] {
  const writable = Object.values(schema)
    .filter(
      p =>
        p.value_write_access === 'all' && hasPropertyEditor(p.field_type_name)
    )
    .map(p => p.property_type_name);
  const hasDisplayName = writable.includes('ahoy_display_name');
  const rest = writable.filter(n => n !== 'ahoy_display_name');
  return hasDisplayName ? ['ahoy_display_name', ...rest] : rest;
}

/**
 * Names of the associations to offer in a create form for `entityType`: the
 * associations where `entityType` is the source side and the user may write,
 * excluding pipeline/stage infrastructure associations and notes (notes are
 * managed via the Notes tab, not the create form). Mirrors `GenericCrmList`.
 *
 * Note: `CrmCreateForm` applies its own domain-kind filter on top for custom
 * (`org_*`) types, so reminder/utility associations are removed there too.
 */
export function deriveCreateAssociationNames(
  associationTypes: readonly AssociationType[],
  entityType: string,
  entityKindMap: ReadonlyMap<string, readonly string[]>
): string[] {
  const selfKinds = getEntityKinds(entityType, entityKindMap);
  return associationTypes
    .filter(at => {
      if (!associationMatchesSource(at, entityType, selfKinds)) {
        return false;
      }
      if (at.value_write_access !== 'all') {
        return false;
      }
      // entityType is the source side here, so the counterpart is the target.
      const targetKinds = counterpartEntityKinds(at, entityType, entityKindMap);
      if (targetKinds.includes('pipeline') || targetKinds.includes('stage')) {
        return false;
      }
      if (at.target_entity_type_name === 'ahoy_note') {
        return false;
      }
      return true;
    })
    .map(at => at.association_type_name);
}
